import { useCallback, useMemo, useRef, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { queryParameters } from '~constants';
import { Organisation, OrganisationsService } from '~features/organisations';
import { IdReference } from '~interfaces';

/**
 * A hook to sync the organisation to the url. Only the id is synchronized, so
 * this also provides a mechanism to the organisation from the backend based on
 * the id.
 * There should be one source of truth, and that is the id in the url! So other
 * variables used are only there to support that system.
 * @param defaultOrganisation
 * @returns
 */
const useUrlSearchParamsOrganisation = (
	defaultOrganisation?: Organisation | null,
	disableUrlSync = false
): [Organisation | IdReference | null, (value: Organisation | null) => void] => {
	const [searchParams, setSearchParams] = useSearchParams();

	const service = useRef(new OrganisationsService());

	const organisationId = searchParams.get(queryParameters.Organisation);

	const { data } = useSWR(
		organisationId != null ? [service.current.basePath, organisationId] : null,
		([_, args]) => service.current.getOrganisationById(args),
		{
			// We will use this hook over multiple components. This prevent this from overfetching
			// but try to keep it up to date every once in a while. The organisation may change very
			// rarely
			dedupingInterval: 10000, // ms
		}
	);

	const urlOrganisation = useMemo<Organisation | IdReference | null>(() => {
		if (!organisationId) {
			return null;
		}

		return data ?? (organisationId != null ? { id: organisationId } : null);
	}, [organisationId, data]);

	const [internalOrganisation, setInternalOrganisation] = useState<Organisation | null>(
		defaultOrganisation ?? null
	);

	const setOrganisation = useCallback(
		(value: Organisation | null) => {
			if (disableUrlSync) {
				setInternalOrganisation(value);
				return;
			}

			setSearchParams((prev) => {
				if (value == null) {
					prev.delete(queryParameters.Organisation);
				} else {
					prev.set(queryParameters.Organisation, value.id);
				}

				return prev;
			});
		},
		[disableUrlSync, setSearchParams]
	);

	return [!disableUrlSync ? urlOrganisation : internalOrganisation, setOrganisation];
};

export default useUrlSearchParamsOrganisation;
